import { useQuery } from '@tanstack/react-query';

import { getLineChannelBanStatus } from 'api/adminBanStatus';
import { queryKeys } from 'constants/queryKeys';
import { banStatusStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TBanStatusResponse } from 'types';
import { toErrorMessage } from './utils';

type UseAdminBanStatusResult = {
  banStatus: TBanStatusResponse | undefined;
  loading: boolean;
  refreshing: boolean;
  error: string | undefined;
  refresh: () => void;
};

/**
 * 全組織のBAN状況の集計。
 *
 * 一覧の絞り込みではこのクエリを作り直さない（集計は常に全体のため）。
 * 画面を開くたびに取り直し、ユーザー一覧との比較に古い集計を使わない。
 * ウィンドウのフォーカス・再接続では再取得せず、手動の再読み込みにも対応する。
 */
export function useAdminBanStatus(): UseAdminBanStatusResult {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [queryKeys.LINE_CHANNEL_BAN_STATUS, user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: getLineChannelBanStatus,
    staleTime: 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return {
    banStatus: query.data,
    loading: Boolean(user?.uid) && query.isPending,
    refreshing: query.isFetching && !query.isPending,
    error: query.error
      ? toErrorMessage(query.error, banStatusStrings.errors.loadFailed)
      : undefined,
    refresh: () => {
      void query.refetch();
    },
  };
}
